import { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Link,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import { Link as RouterLink } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import { getApprovedSubmissions } from "../api/submissions";
import { hasPermission } from "../utils/permissions";
import { getPrerenderSubmissionList } from "../utils/prerenderData";
import { homeSubmissionsPreviewKey } from "../utils/routeCacheKeys";
import useRouteResource from "../hooks/useRouteResource";
import HomePreviewSkeleton from "./loading/HomePreviewSkeleton";

const PREVIEW_LIMIT = 6;

function SubmissionPreviewMetadata({ item }) {
  if (!item) return null;
  return (
    <Box sx={{ mt: 2, textAlign: "center" }}>
      {item.owner && (
        <Typography variant="subtitle1" gutterBottom>
          {item.owner}
        </Typography>
      )}
      {item.pwarp && (
        <Typography variant="body2" color="text.secondary">
          Pwarp: {item.pwarp}
        </Typography>
      )}
      {item.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {item.description}
        </Typography>
      )}
      {item.buildCredits && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Credits: {item.buildCredits}
        </Typography>
      )}
    </Box>
  );
}

export default function UserSubmissionsPreview() {
  const { player, isAuthenticated } = useContext(AuthContext);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const forbidden =
    isAuthenticated && !hasPermission(player, "submissions.gallery.view");

  const prerenderSubmissions = useMemo(
    () => getPrerenderSubmissionList().slice(0, PREVIEW_LIMIT),
    []
  );

  const {
    data: submissions,
    error,
    isReady,
  } = useRouteResource({
    cacheKey: homeSubmissionsPreviewKey(PREVIEW_LIMIT),
    enabled: !forbidden,
    fetch: async () => {
      const data = await getApprovedSubmissions({ limit: PREVIEW_LIMIT });
      return data.submissions || [];
    },
    prerenderFallback: prerenderSubmissions,
  });

  useEffect(() => {
    if (selectedIndex == null) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "ArrowRight") {
        setSelectedIndex((prev) => (prev + 1) % submissions.length);
      } else if (event.key === "ArrowLeft") {
        setSelectedIndex((prev) => (prev - 1 + submissions.length) % submissions.length);
      } else if (event.key === "Escape") {
        setSelectedIndex(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedIndex, submissions]);

  if (forbidden) {
    return null;
  }

  const submissionItems = submissions || [];
  const selected = selectedIndex != null ? submissionItems[selectedIndex] : null;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Community Screenshots
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Player-submitted builds and scenery, approved for the gallery.
      </Typography>

      {!isReady ? (
        <HomePreviewSkeleton count={PREVIEW_LIMIT} />
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : submissionItems.length === 0 ? (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No approved submissions yet. Be the first to share a screenshot.
        </Typography>
      ) : (
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {submissionItems.map((item, index) => (
            <Grid item xs={4} sm={4} md={2} key={item.id}>
              <Box
                onClick={() => setSelectedIndex(index)}
                sx={{
                  display: "block",
                  aspectRatio: "1 / 1",
                  overflow: "hidden",
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                  cursor: "pointer",
                  "&:hover": { boxShadow: 2 },
                }}
              >
                <Box
                  component="img"
                  src={item.thumbnailUrl || item.originalUrl}
                  alt={item.owner || "Community screenshot"}
                  loading="lazy"
                  sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
        <Link
          component={RouterLink}
          to="/user-submissions"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontWeight: 600 }}
        >
          View all submissions
          <ArrowForwardIcon fontSize="small" />
        </Link>
        <Link
          component={RouterLink}
          to="/submissions/screenshot"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontWeight: 600 }}
        >
          Submit a screenshot
          <ArrowForwardIcon fontSize="small" />
        </Link>
      </Box>

      <Dialog
        open={selectedIndex != null}
        onClose={() => setSelectedIndex(null)}
        maxWidth="lg"
        fullWidth
      >
        <IconButton
          aria-label="close"
          onClick={() => setSelectedIndex(null)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            zIndex: 1,
            color: "white",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.7)" },
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ position: "relative" }}>
          {selected && (
            <>
              <img
                src={selected.originalUrl || selected.thumbnailUrl}
                alt={selected.owner || "Community screenshot"}
                style={{ width: "100%", height: "auto", display: "block" }}
              />
              <SubmissionPreviewMetadata item={selected} />
            </>
          )}
          {submissionItems.length > 1 && selectedIndex != null && (
            <>
              <IconButton
                aria-label="previous"
                onClick={() =>
                  setSelectedIndex((prev) => (prev - 1 + submissionItems.length) % submissionItems.length)
                }
                sx={{
                  position: "absolute",
                  left: 8,
                  top: "50%",
                  color: "white",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.7)" },
                }}
              >
                <ArrowBackIosIcon />
              </IconButton>
              <IconButton
                aria-label="next"
                onClick={() => setSelectedIndex((prev) => (prev + 1) % submissionItems.length)}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  color: "white",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.7)" },
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
